/**
 * Page Anchor Navigation with Splide Slider
 * Initializes a horizontal slider on mobile with auto-width slides
 */

(function () {
  'use strict';

  class PageAnchorNavigation {
    constructor(section) {
      this.section = section;
      this.sliderElement = section.querySelector('[data-page-anchor-slider]');
      this.anchorElement = document.getElementById('page-anchor-navigation-anchor');
      this.headerElement = document.querySelector('[data-header-section]');
      this.observer = null;
      this.sectionObservers = [];
      this.links = [];
      this.sections = [];
      this.isScrolling = false;
      this.scrollTimeout = null;

      if (!this.sliderElement) {
        console.warn('PageAnchorNavigation: Slider element not found');
        return;
      }

      this.splide = null;
      this.init();
    }

    init() {
      // Check if Splide is available
      if (typeof Splide === 'undefined') {
        console.error('PageAnchorNavigation: Splide is not loaded');
        return;
      }

      this.initSlider();
      this.setupResizeHandler();
      this.setupIntersectionObserver();
      this.setupSectionTracking();
      this.setupClickHandlers();
      this.setupKeyboardNavigation();
      this.setupStickyFormButtons();
    }

    initSlider() {
      // Initialize Splide with auto-width configuration
      this.splide = new Splide(this.sliderElement, {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        autoWidth: true,
        focus: 0,
        slideFocus: false,
        omitEnd: true,
        pagination: false,
        arrows: false,
        isNavigation: true,
        drag: true,
        gap: '16px',
        breakpoints: {
          1099: {
            // Enable on mobile only
            destroy: false,
          },
          1100: {
            // Destroy slider on desktop
            destroy: true,
          }
        }
      });

      this.splide.mount();

      // Remove tabindex from splide slides to prevent focus
      this.splide.on('mounted', () => {
        const slides = this.sliderElement.querySelectorAll('.splide__slide');
        slides.forEach(slide => {
          slide.setAttribute('tabindex', '-1');
        });
      });
    }

    setupResizeHandler() {
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          // Reinitialize on resize to handle breakpoint changes
          if (this.splide) {
            this.splide.refresh();
          }
        }, 250);
      });
    }

    setupIntersectionObserver() {
      // Only set up if anchor element exists
      if (!this.anchorElement) {
        console.warn('PageAnchorNavigation: Anchor element #page-anchor-navigation-anchor not found');
        // Show navigation by default if anchor doesn't exist
        this.section.classList.add('is-active');
        this.setFocusability(true);
        return;
      }

      // Initially hide and make unfocusable
      this.setFocusability(false);

      // Use scroll event for more reliable position tracking
      this.checkNavigationVisibility = () => {
        const anchorRect = this.anchorElement.getBoundingClientRect();
        const shouldShowNav = anchorRect.top <= 100;

        if (shouldShowNav) {
          // Anchor has reached/passed the top - show navigation and hide header
          if (!this.section.classList.contains('is-active')) {
            this.section.classList.add('is-active');
            this.setFocusability(true);
            if (this.headerElement) {
              this.headerElement.classList.add('is-hidden');
            }
          }
        } else {
          // Anchor is below the top - hide navigation and show header
          if (this.section.classList.contains('is-active')) {
            this.section.classList.remove('is-active');
            this.setFocusability(false);
            if (this.headerElement) {
              this.headerElement.classList.remove('is-hidden');
            }
          }
        }
      };

      // Check on scroll with throttling
      this.navVisibilityHandler = this.throttle(this.checkNavigationVisibility, 16); // ~60fps
      window.addEventListener('scroll', this.navVisibilityHandler, { passive: true });

      // Initial check
      this.checkNavigationVisibility();
    }

    setFocusability(isFocusable) {
      // Get all focusable elements within the navigation
      const focusableElements = this.section.querySelectorAll(
        'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (isFocusable) {
        // Make navigation focusable
        this.section.removeAttribute('inert');
        focusableElements.forEach(el => {
          const originalTabIndex = el.getAttribute('data-original-tabindex');
          if (originalTabIndex !== null) {
            if (originalTabIndex === 'null') {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', originalTabIndex);
            }
            el.removeAttribute('data-original-tabindex');
          } else {
            el.removeAttribute('tabindex');
          }
        });
      } else {
        // Make navigation unfocusable using inert if supported, otherwise tabindex
        if ('inert' in HTMLElement.prototype) {
          this.section.setAttribute('inert', '');
        } else {
          // Fallback for browsers that don't support inert
          focusableElements.forEach(el => {
            const currentTabIndex = el.getAttribute('tabindex');
            el.setAttribute('data-original-tabindex', currentTabIndex === null ? 'null' : currentTabIndex);
            el.setAttribute('tabindex', '-1');
          });
        }
      }
    }

    setupSectionTracking() {
      // Get all navigation links from BOTH desktop and mobile layouts
      const desktopLinks = this.section.querySelectorAll('.page-anchor-navigation__wrapper--desktop .page-anchor-navigation__link');
      const mobileLinks = this.section.querySelectorAll('.page-anchor-navigation__wrapper--mobile .page-anchor-navigation__link');

      // Combine both sets of links
      this.allLinks = {
        desktop: Array.from(desktopLinks),
        mobile: Array.from(mobileLinks)
      };


      // Use desktop links as the primary source for building sections array
      this.links = Array.from(desktopLinks);

      if (this.links.length === 0) {
        console.warn('PageAnchorNavigation: No navigation links found');
        return;
      }

      // Build array of target sections for each navigation link
      this.links.forEach((link, index) => {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) {
          console.warn('PageAnchorNavigation: Invalid href on link', link);
          return;
        }

        const targetId = href.substring(1);
        const targetSection = document.getElementById(targetId);

        if (targetSection) {
          this.sections.push({
            id: targetId,
            element: targetSection,
            desktopLink: desktopLinks[index],
            mobileLink: mobileLinks[index],
            index: index
          });
        } else {
          console.warn('PageAnchorNavigation: Target section not found for', targetId);
        }
      });

      if (this.sections.length === 0) {
        console.warn('PageAnchorNavigation: No target sections found');
        return;
      }


      // Set up scroll listener for active section detection
      this.handleScroll = this.throttle(() => {
        if (!this.isScrolling) {
          this.updateActiveSection();
        }
      }, 50); // Reduced from 100ms to 50ms for faster response

      window.addEventListener('scroll', this.handleScroll, { passive: true });

      // Initial check after a small delay to ensure layout is ready
      setTimeout(() => {
        this.updateActiveSection();
      }, 100);
    }

    updateActiveSection() {
      // Get navigation offset (height of sticky nav)
      const navOffset = this.section.offsetHeight;
      const scrollPosition = window.pageYOffset + navOffset + 50; // Increased buffer for earlier activation


      // Find the current section based on scroll position
      let currentSection = null;

      // Loop through sections from last to first to find the active one
      for (let i = this.sections.length - 1; i >= 0; i--) {
        const section = this.sections[i];
        const sectionTop = section.element.offsetTop;


        // Check if we've scrolled past this section's top
        if (scrollPosition >= sectionTop) {
          currentSection = section;
          break;
        }
      }

      // If no section found (at top of page), use first section
      if (!currentSection && this.sections.length > 0) {
        currentSection = this.sections[0];
      }

      // Update active state if changed
      if (currentSection) {
        this.setActiveLink(currentSection);
      } else {
      }
    }

    // Throttle function to limit scroll event frequency
    throttle(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    setupClickHandlers() {
      // Handle clicks on all anchor links (both desktop and mobile)
      const allClickableLinks = [
        ...this.allLinks.desktop,
        ...this.allLinks.mobile
      ];

      allClickableLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();

          const href = link.getAttribute('href');
          if (!href || !href.startsWith('#')) {
            return;
          }

          const targetId = href.substring(1);
          const targetSection = document.getElementById(targetId);

          if (targetSection) {
            this.scrollToSection(targetSection);
          }
        });
      });
    }

    setupStickyFormButtons() {
      // Find all CTA buttons with data-open-sticky-form attribute
      const stickyFormButtons = this.section.querySelectorAll('[data-open-sticky-form]');

      if (stickyFormButtons.length === 0) {
        return;
      }

      // Find the sticky PDP form section
      const stickyFormSection = document.querySelector('[data-sticky-pdp-form]');

      if (!stickyFormSection) {
        // Sticky form doesn't exist - set up fallback URL navigation
        console.log('PageAnchorNavigation: Sticky form not found, using fallback URL navigation');
        this.setupFallbackNavigation(stickyFormButtons);
        return;
      }

      // Add click handlers to all sticky form buttons
      stickyFormButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent event from bubbling to document (sticky form's outside click handler)
          this.openStickyForm(stickyFormSection);
        });
      });
    }

    setupFallbackNavigation(buttons) {
      buttons.forEach(button => {
        const fallbackUrl = button.dataset.fallbackUrl;
        const fallbackTarget = button.dataset.fallbackTarget;

        if (!fallbackUrl || fallbackUrl === '#') {
          console.warn('PageAnchorNavigation: No valid fallback URL provided for button');
          return;
        }

        button.addEventListener('click', (e) => {
          e.preventDefault();

          if (fallbackTarget === '_blank') {
            window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = fallbackUrl;
          }
        });
      });
    }

    openStickyForm(stickyFormSection) {
      // Check if the sticky form is visible
      if (!stickyFormSection || stickyFormSection.style.display === 'none') {
        console.warn('PageAnchorNavigation: Sticky form is not visible');
        return;
      }

      // Try to find and trigger the existing openForm method from StickyPdpForm instance
      // First check if the sticky form has a toggle button we can click
      const toggleButton = stickyFormSection.querySelector('[data-toggle-button]');

      if (toggleButton) {
        // Check if form is already expanded
        const isExpanded = toggleButton.getAttribute('aria-expanded') === 'true';

        if (!isExpanded) {
          // Programmatically click the toggle button to open the form
          toggleButton.click();

          // Optionally scroll to the sticky form
          const stickyFormRect = stickyFormSection.getBoundingClientRect();

          // If sticky form is above viewport or near the bottom, scroll to it
          if (stickyFormRect.top < 0 || stickyFormRect.bottom > window.innerHeight) {
            window.scrollTo({
              top: window.pageYOffset + stickyFormRect.top - 100,
              behavior: 'smooth'
            });
          }
        }
      } else {
        console.warn('PageAnchorNavigation: Could not find toggle button in sticky form');
      }
    }

    setupKeyboardNavigation() {
      // Get all navigation links (both desktop and mobile)
      const desktopLinks = this.allLinks?.desktop || [];
      const mobileLinks = this.allLinks?.mobile || [];

      // Setup keyboard navigation for desktop links
      this.setupKeyboardForLinks(desktopLinks);

      // Setup keyboard navigation for mobile links
      this.setupKeyboardForLinks(mobileLinks);
    }

    setupKeyboardForLinks(links) {
      if (!links || links.length === 0) return;

      links.forEach((link, index) => {
        link.addEventListener('keydown', (e) => {
          let targetIndex = -1;

          switch (e.key) {
            case 'Enter':
            case ' ': // Space bar
              e.preventDefault();
              // Trigger the link's navigation
              const href = link.getAttribute('href');
              if (href && href.startsWith('#')) {
                const targetId = href.substring(1);
                const targetSection = document.getElementById(targetId);
                if (targetSection) {
                  this.scrollToSection(targetSection);
                }
              }
              return;

            case 'ArrowRight':
            case 'ArrowDown':
              e.preventDefault();
              targetIndex = (index + 1) % links.length;
              break;

            case 'ArrowLeft':
            case 'ArrowUp':
              e.preventDefault();
              targetIndex = (index - 1 + links.length) % links.length;
              break;

            case 'Home':
              e.preventDefault();
              targetIndex = 0;
              break;

            case 'End':
              e.preventDefault();
              targetIndex = links.length - 1;
              break;

            default:
              return; // Exit if not a navigation key
          }

          // Focus the target link
          if (targetIndex >= 0 && links[targetIndex]) {
            links[targetIndex].focus();

            // If on mobile and splide is active, move splide to the focused item
            if (this.splide && !this.splide.state.is(Splide.STATES.DESTROYED)) {
              this.splide.go(targetIndex);
            }
          }
        });
      });
    }

    scrollToSection(targetSection) {
      // Set scrolling flag to prevent scroll listener interference
      this.isScrolling = true;

      // Calculate offset (account for sticky header or navigation)
      const offset = this.section.offsetHeight || 0;
      const targetPosition = targetSection.getBoundingClientRect().top + window.pageYOffset - offset;

      // Smooth scroll to target
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      // Reset scrolling flag after animation completes
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
        this.updateActiveSection();
      }, 800); // Reduced from 1000ms to 800ms for faster updates
    }

    setActiveLink(sectionData) {

      // Check if this link is already active
      const desktopAlreadyActive = sectionData.desktopLink && sectionData.desktopLink.classList.contains('is-active');
      const mobileAlreadyActive = sectionData.mobileLink && sectionData.mobileLink.classList.contains('is-active');

      if (desktopAlreadyActive && mobileAlreadyActive) {
        return;
      }

      // Remove active class from all links (both desktop and mobile)
      this.sections.forEach(section => {
        if (section.desktopLink) {
          section.desktopLink.classList.remove('is-active');
        }
        if (section.mobileLink) {
          section.mobileLink.classList.remove('is-active');
        }
      });

      // Add active class to current link (both desktop and mobile versions)
      if (sectionData.desktopLink) {
        sectionData.desktopLink.classList.add('is-active');
      }
      if (sectionData.mobileLink) {
        sectionData.mobileLink.classList.add('is-active');
      }

      // Move splide to active slide if on mobile
      if (this.splide && !this.splide.state.is(Splide.STATES.DESTROYED)) {
        this.splide.go(sectionData.index);
      }
    }

    destroy() {
      if (this.splide) {
        this.splide.destroy();
        this.splide = null;
      }
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      // Remove scroll listeners
      if (this.handleScroll) {
        window.removeEventListener('scroll', this.handleScroll);
      }
      if (this.navVisibilityHandler) {
        window.removeEventListener('scroll', this.navVisibilityHandler);
      }
      // Clear timeouts
      clearTimeout(this.scrollTimeout);

      // Disconnect all section observers (if any legacy observers exist)
      this.sectionObservers.forEach(observer => {
        observer.disconnect();
      });
      this.sectionObservers = [];

      // Reset visibility and focusability on destroy
      this.section.style.display = '';
      this.setFocusability(true);
      if (this.headerElement) {
        this.headerElement.classList.remove('is-hidden');
      }

      // Remove active classes
      this.sections.forEach(section => {
        if (section.desktopLink) {
          section.desktopLink.classList.remove('is-active');
        }
        if (section.mobileLink) {
          section.mobileLink.classList.remove('is-active');
        }
      });
    }
  }

  // Store single instance
  let pageAnchorNavigationInstance = null;

  // Initialize page anchor navigation (only one per page)
  function initPageAnchorNavigation() {
    const section = document.querySelector('.page-anchor-navigation');

    if (!section) {
      return;
    }

    // Check if already initialized
    if (section.dataset.initialized === 'true') {
      return;
    }

    pageAnchorNavigationInstance = new PageAnchorNavigation(section);
    section.dataset.initialized = 'true';
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPageAnchorNavigation);
  } else {
    initPageAnchorNavigation();
  }

  // Reinitialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function (event) {
    const section = event.target.querySelector('.page-anchor-navigation');
    if (section) {
      // Destroy existing instance if any
      if (pageAnchorNavigationInstance) {
        pageAnchorNavigationInstance.destroy();
      }

      section.dataset.initialized = 'false';
      pageAnchorNavigationInstance = new PageAnchorNavigation(section);
      section.dataset.initialized = 'true';
    }
  });

  // Clean up on section unload
  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector('.page-anchor-navigation');
    if (section && pageAnchorNavigationInstance) {
      pageAnchorNavigationInstance.destroy();
      pageAnchorNavigationInstance = null;
    }
  });

})();
